import {
  PositionType,
  type ProfileEditReviewItemRes,
  ReviewItemStatus,
  v1PeopleProfileEditsCreate,
} from '@on3/api';
import { PersonAutoComplete } from '@on3/ui-lib/components/AutoComplete/PersonAutoComplete';
import { Avatar } from '@on3/ui-lib/components/Avatar';
import { Form } from '@on3/ui-lib/components/Form';
import { Select } from '@on3/ui-lib/components/Select/Select';
import { externalApi, useToast } from '@on3/ui-lib/index';
import {
  type TopInterest,
  topInterestSchema,
  useRecruitmentStatusOptions,
} from '@public/components/Elite/Profile/Edits/TopInterests';
import { usePlayerProfile } from '@public/contexts/PlayerProfile/PlayerProvider';
import { createTitle } from '@public/utils/meta';
import { useState } from 'react';

import styles from './UpdateTeamModal.module.scss';

interface IUpdateTeamModalProps {
  onComplete: () => void;
  updateEdits: (reviewItem: ProfileEditReviewItemRes) => void;
  interest: TopInterest;
  protectedTeam?: TopInterest;
}

export const UpdateTeamModal = ({
  onComplete = () => {},
  updateEdits = () => {},
  interest,
  protectedTeam,
}: IUpdateTeamModalProps) => {
  const {
    state: { player },
  } = usePlayerProfile();
  const { notify } = useToast();
  const recruitmentStatusOptions = useRecruitmentStatusOptions(
    protectedTeam,
    interest,
  );

  const [values, setValues] = useState<TopInterest>(interest);

  const handleUpdate = (field: keyof TopInterest, value: any) => {
    setValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async ({ $type, editType, ...edit }: TopInterest) => {
    if (!edit.recruitmentInterestKey) {
      notify(
        'Unable to update due to stale data. Please refresh and try again.',
        {
          type: 'error',
        },
      );
    }

    try {
      const updatedInterest = {
        $type,
        editType,
        ...edit,
      };

      const { reviewItem } = await v1PeopleProfileEditsCreate(
        externalApi,
        player.key,
        {
          edit: updatedInterest,
        },
      );

      notify('Team updated successfully.', { type: 'success' });

      if (reviewItem.status === ReviewItemStatus.Pending) {
        updateEdits(reviewItem);
      }

      // TODO: Update this to use the new API schema
      onComplete();
    } catch (error) {
      notify('Failed to update team.', { type: 'error' });
    }
  };

  const isValid = !!values.organizationKey;

  return (
    <Form
      className={styles.block}
      onSubmit={handleSubmit}
      schema={topInterestSchema}
      values={values}
    >
      {({ processing, errors }) => (
        <>
          <header className={styles.header}>
            <h2>Update Team</h2>
          </header>
          <div className={styles.team}>
            <span className={styles.label}>Team</span>
            <div className={styles.teamInner}>
              <figure className={styles.avatar}>
                <Avatar
                  altText={values.organizationName}
                  height={20}
                  source={values.organizationAssetUrl}
                  teamAvatar
                  width={20}
                />
              </figure>
              <span className={styles.name}>{values.organizationName}</span>
            </div>
          </div>
          <Select
            label="Recruitment Status"
            name="recruitmentStatus"
            onChange={(v) => handleUpdate('recruitmentStatus', v.target.value)}
            options={recruitmentStatusOptions}
            value={values.recruitmentStatus}
          />
          <PersonAutoComplete
            disabled={!values.organizationKey}
            error={errors.primaryPersonKey}
            id="recruitedBy"
            label="Recruited By"
            limit={1}
            name="primaryPersonKey"
            onChange={(val) => {
              handleUpdate('primaryPersonKey', val[0]?.key);
              handleUpdate('primaryPersonName', val[0]?.label);
            }}
            optionTemplate={(c) => ({
              key: c.key,
              label: createTitle(
                c.firstName,
                c.lastName,
                c.currentOrganization?.name &&
                  `(${c.currentOrganization?.name})`,
              ),
              selectedLabel: createTitle(c.firstName, c.lastName),
            })}
            placeholder="Search for recruiter..."
            searchParams={{
              positionTypes: [PositionType.Coach],
              currentOrganizationKey: values.organizationKey,
            }}
            value={[
              {
                key: values.primaryPersonKey ?? 0,
                label: values.primaryPersonName ?? '',
              },
            ].filter((c) => c?.key)}
          />
          <button disabled={processing || !isValid} type="submit">
            Save
          </button>
        </>
      )}
    </Form>
  );
};
